import * as React from "react";
import { AnalyticsSpinnerProps } from "../../types/stripe";

/**
 * AnalyticsSpinner Component
 * 
 * A flexible loading spinner with overlay support and customizable messaging
 * designed for analytics dashboard loading states.
 */
const AnalyticsSpinner: React.FC<AnalyticsSpinnerProps> = ({
  size = 'md',
  color = 'primary',
  overlay = false,
  message = 'Loading...',
  className = "",
  id
}) => {
  const sizeClasses = {
    sm: 'spinner-sm',
    md: 'spinner-md',
    lg: 'spinner-lg'
  };

  const colorClasses = {
    primary: 'text-primary',
    secondary: 'text-secondary',
    success: 'text-success',
    danger: 'text-danger',
    warning: 'text-warning',
    info: 'text-info',
    light: 'text-light',
    dark: 'text-dark'
  };

  const spinnerSizes = {
    sm: { width: '1rem', height: '1rem', borderWidth: '2px' },
    md: { width: '2rem', height: '2rem', borderWidth: '3px' },
    lg: { width: '3rem', height: '3rem', borderWidth: '4px' }
  };

  const SpinnerContent = () => (
    <div className={`analytics-spinner ${className}`.trim()} id={id}>
      <div className="spinner-container">
        <div 
          className={`spinner-border ${colorClasses[color] || `text-${color}`} ${sizeClasses[size]}`}
          role="status"
          style={spinnerSizes[size]}
        >
          <span className="visually-hidden">Loading...</span>
        </div>
        
        {message && (
          <div className="spinner-message">
            <span className={`text-${color === 'light' ? 'dark' : 'muted'}`}>
              {message}
            </span>
          </div>
        )}
      </div>

      <style>{`
        .analytics-spinner {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .spinner-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          padding: 2rem;
        }

        .spinner-border {
          border-right-color: transparent !important;
          animation: spinner-border-animation 0.75s linear infinite;
        }

        .spinner-sm {
          font-size: 0.875rem;
        }

        .spinner-md {
          font-size: 1rem;
        }

        .spinner-lg {
          font-size: 1.25rem;
        }

        .spinner-message {
          text-align: center;
          font-size: ${size === 'sm' ? '0.875rem' : size === 'lg' ? '1.125rem' : '1rem'};
          font-weight: 500;
        }

        @keyframes spinner-border-animation {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        /* Pulsing effect for message */
        .spinner-message span {
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .spinner-border {
            animation: none;
            border: ${spinnerSizes[size].borderWidth} solid currentColor;
            border-radius: 50%;
          }
          
          .spinner-message span {
            animation: none;
          }
        }

        /* High contrast mode support */
        @media (prefers-contrast: high) {
          .spinner-border {
            border-width: ${parseInt(spinnerSizes[size].borderWidth) + 1}px;
          }
          
          .spinner-message span {
            font-weight: 600;
          }
        }
      `}</style>
    </div>
  );

  if (overlay) {
    return (
      <>
        <div className="analytics-spinner-overlay">
          <div className="overlay-backdrop">
            <SpinnerContent />
          </div>
        </div>

        <style>{`
          .analytics-spinner-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 1050;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .overlay-backdrop {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.9);
            backdrop-filter: blur(2px);
            display: flex;
            align-items: center;
            justify-content: center;
            animation: fadeIn 0.2s ease-out;
          }

          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          /* Dark theme support for overlay */
          @media (prefers-color-scheme: dark) {
            .overlay-backdrop {
              background: rgba(0, 0, 0, 0.8);
            }
          }

          /* Reduced motion support */
          @media (prefers-reduced-motion: reduce) {
            .overlay-backdrop {
              animation: none;
            }
          }
        `}</style>
      </>
    );
  }

  return <SpinnerContent />;
};

// Additional specialized spinner variants
export const AnalyticsCardSpinner: React.FC<Omit<AnalyticsSpinnerProps, 'overlay'>> = (props) => (
  <div className="card-body d-flex align-items-center justify-content-center" style={{ minHeight: '200px' }}>
    <AnalyticsSpinner {...props} />
  </div>
);

export const AnalyticsTableSpinner: React.FC<Omit<AnalyticsSpinnerProps, 'overlay'>> = (props) => (
  <div className="text-center py-5">
    <AnalyticsSpinner {...props} size="lg" />
  </div>
);

export const AnalyticsChartSpinner: React.FC<Omit<AnalyticsSpinnerProps, 'overlay'> & { height?: number }> = ({ 
  height = 400, 
  ...props 
}) => (
  <div 
    className="d-flex align-items-center justify-content-center"
    style={{ height: `${height}px` }}
  >
    <AnalyticsSpinner {...props} />
  </div>
);

export const AnalyticsInlineSpinner: React.FC<Omit<AnalyticsSpinnerProps, 'overlay' | 'message'>> = (props) => (
  <div className="d-inline-flex align-items-center">
    <div 
      className={`spinner-border text-${props.color || 'primary'}`}
      role="status" 
      style={{ width: '1rem', height: '1rem', borderWidth: '2px' }}
    >
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

AnalyticsSpinner.displayName = 'AnalyticsSpinner';

export default AnalyticsSpinner;